import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import UserLayout from '@/components/Layout/UserLayout';
import { Button, Callout, Card, ConfirmationDialog, DataTable, Disclosure, FormInput, FormSelect, StatusBadge } from '@/components/UI';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { hydrateTradeLogicDraftFromBackend } from '@/features/tradeLogicAuthoring/api';
import { collectFeatureKeys } from '@/features/tradeLogicAuthoring/components/importedExpressionFormat';
import TradeLogicRuleTree from '@/features/tradeLogicAuthoring/components/TradeLogicRuleTree';
import { useTradeLogicRegistryFeatures } from '@/features/tradeLogicAuthoring/registry';
import { useAppQuery } from '@/hooks/useAppQuery';
import ActionMenu, { type ActionMenuItem } from './components/ActionMenu';
import PromotionReceiptCard from './components/PromotionReceiptCard';
import VariantGridModal from './components/VariantGridModal';
import { STANDARD_DURATIONS } from '@einhaus/project314-shared';
import type { BarDuration } from '@einhaus/project314-types';
import {
    buildIdeaLabRulesEditorPath,
    describeRunLenses,
    diffVariantRules,
    formatWallClock,
    getRunStatusTone,
    getVariantLifecycleTone,
    parsePositiveInt,
    RUN_STATUS_LABELS,
    VARIANT_LIFECYCLE_LABELS,
    type IdeaLabFreezeResult,
    type IdeaLabVariantDetail
} from './ideaLabUtils';
import { buildRunVariantPath } from './reportView';
import { describeRunWindowShort, formatWallClockShort } from './studyView';
import { useMakeVariation } from './useMakeVariation';

type VariantArtifact = IdeaLabVariantDetail['artifacts'][number];

const shortHash = (value: string | null): string => (value === null ? '—' : value.slice(0, 12));

const JsonBlock = ({ value }: { value: unknown }) => (
    <pre className="max-h-[32rem] overflow-auto rounded border border-zinc-800 bg-black/40 p-3 text-xs leading-relaxed text-zinc-300">
        {JSON.stringify(value, null, 2)}
    </pre>
);

const Fact = ({ label, children }: { label: string; children: ReactNode }) => (
    <>
        <dt className="text-zinc-500">{label}</dt>
        <dd className="break-all text-zinc-300">{children}</dd>
    </>
);

/**
 * One variant: what its rules say, what it changed from its parent, and every run that evaluated
 * it, with identity and compiled artifacts folded into Technical details. Drafts are edited in the
 * rule editor; a frozen variant is changed by making a variation, which keeps it as the parent.
 */
const IdeaLabVariant = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { variantId: variantIdParam } = useParams();
    const variantId = parsePositiveInt(variantIdParam ?? null);

    const [label, setLabel] = useState('');
    const [description, setDescription] = useState('');
    const [primaryDuration, setPrimaryDuration] = useState<BarDuration>('5m');
    const [confirmFreeze, setConfirmFreeze] = useState(false);
    const [confirmArchive, setConfirmArchive] = useState(false);
    const [sweepOpen, setSweepOpen] = useState(false);
    const [freezeResult, setFreezeResult] = useState<IdeaLabFreezeResult | null>(null);

    const variantQuery = useAppQuery(
        {
            ...trpc.ideaLab.getVariant.queryOptions({ variantId: variantId ?? 0 }),
            enabled: variantId !== null
        },
        { fallbackErrorMessage: 'Failed to load the variant' }
    );
    const variant = variantQuery.data ?? null;
    const parentQuery = useAppQuery(
        {
            ...trpc.ideaLab.getVariant.queryOptions({ variantId: variant?.parentVariantId ?? 0 }),
            enabled: variant !== null && variant.parentVariantId !== null
        },
        { fallbackErrorMessage: 'Failed to load the parent variant' }
    );
    const parent = variant?.parentVariantId !== null ? (parentQuery.data ?? null) : null;
    const studyQuery = useAppQuery(
        {
            ...trpc.ideaLab.getStudy.queryOptions({ studyId: variant?.studyId ?? 0 }),
            enabled: variant !== null
        },
        { fallbackErrorMessage: 'Failed to load the study' }
    );
    const study = studyQuery.data ?? null;
    const runsQuery = useAppQuery(
        {
            ...trpc.ideaLab.listRuns.queryOptions({ studyId: variant?.studyId ?? 0 }),
            enabled: variant !== null
        },
        { fallbackErrorMessage: 'Failed to load runs' }
    );
    const promotionQuery = useAppQuery(
        {
            ...trpc.ideaLab.getPromotion.queryOptions({ promotionId: variant?.promotionId ?? 0 }),
            enabled: variant !== null && variant.promotionId !== null
        },
        { fallbackErrorMessage: 'Failed to load the promotion' }
    );

    useEffect(() => {
        if (!variant) return;

        setLabel(variant.label);
        setDescription(variant.description ?? '');
        setPrimaryDuration(variant.primaryDuration);
    }, [variant]);

    const ruleDraft = useMemo(() => (variant ? hydrateTradeLogicDraftFromBackend(variant.authoringPayload) : null), [variant]);
    const diffFeatureKeys = useMemo(
        () => (variant && parent ? collectFeatureKeys([variant.authoringPayload.rootNode, parent.authoringPayload.rootNode]) : []),
        [variant, parent]
    );
    const diffFeaturesQuery = useTradeLogicRegistryFeatures({ featureKeys: diffFeatureKeys }, { enabled: diffFeatureKeys.length > 0 });
    const featureLabels = useMemo(
        () => new Map(diffFeaturesQuery.data.map((feature) => [feature.featureKey, feature.displayLabel ?? feature.label])),
        [diffFeaturesQuery.data]
    );
    const ruleDiff = useMemo(
        () =>
            variant && parent
                ? diffVariantRules(
                      variant.authoringPayload.rootNode,
                      parent.authoringPayload.rootNode,
                      (featureKey) => featureLabels.get(featureKey) ?? featureKey
                  )
                : null,
        [variant, parent, featureLabels]
    );
    const variantRuns = useMemo(
        () => (runsQuery.data ?? []).filter((run) => variantId !== null && run.variantIds.includes(variantId)),
        [runsQuery.data, variantId]
    );

    const invalidate = () =>
        Promise.all([
            queryClient.invalidateQueries(trpc.ideaLab.getVariant.pathFilter()),
            queryClient.invalidateQueries(trpc.ideaLab.getStudy.pathFilter())
        ]);

    const updateMutation = useMutation({
        ...trpc.ideaLab.updateVariant.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async () => {
            showToast('Variant updated', 'success');
            await invalidate();
        }
    });
    const freezeMutation = useMutation({
        ...trpc.ideaLab.freezeVariant.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async (result) => {
            showToast(`Froze "${result.variant.label}"`, 'success');
            setConfirmFreeze(false);
            setFreezeResult(result);
            await invalidate();
        }
    });
    const ensureArtifactMutation = useMutation({
        ...trpc.ideaLab.ensureArtifactForCurrentCompiler.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async (result) => {
            showToast(
                result.created
                    ? `Compiled artifact #${result.artifact.id} for the current compiler`
                    : `Artifact #${result.artifact.id} already matches the current compiler`,
                'success'
            );
            await invalidate();
        }
    });
    const archiveMutation = useMutation({
        ...trpc.ideaLab.archiveVariant.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async (archived) => {
            showToast(`Archived "${archived.label}"`, 'success');
            setConfirmArchive(false);
            await invalidate();
            void navigate(`/user/ideaLab/study/${archived.studyId}`);
        }
    });
    const variation = useMakeVariation();

    const isDraft = variant?.lifecycle === 'draft';
    const isFrozen = variant?.lifecycle === 'frozen';
    const metadataDirty =
        variant !== null &&
        (label.trim() !== variant.label ||
            (description.trim() === '' ? null : description.trim()) !== variant.description ||
            primaryDuration !== variant.primaryDuration);

    const saveMetadata = () => {
        if (!variant) throw new Error('Cannot save a variant before it has loaded');
        if (label.trim() === '') return;

        updateMutation.mutate({
            variantId: variant.id,
            label: label.trim(),
            description: description.trim() === '' ? null : description.trim(),
            primaryDuration
        });
    };

    const makeVariation = () => {
        if (!variant || !study) throw new Error('Cannot make a variation before the variant and its study have loaded');

        variation.makeVariation(
            variant,
            study.variants.map((candidate) => candidate.variantKey)
        );
    };

    const moreActions: ActionMenuItem[] = variant
        ? [
              ...(isFrozen
                  ? [
                        {
                            label: 'Recompile for the current engine',
                            description: 'Build an artifact with today’s compiler so new runs can use it',
                            onSelect: () => ensureArtifactMutation.mutate({ variantId: variant.id }),
                            disabled: ensureArtifactMutation.isPending
                        }
                    ]
                  : []),
              ...(variant.lifecycle !== 'archived'
                  ? [
                        {
                            label: 'Archive',
                            description: 'Keep it readable but stop it joining new runs',
                            onSelect: () => setConfirmArchive(true),
                            tone: 'danger' as const
                        }
                    ]
                  : [])
          ]
        : [];

    const artifactColumns: ColumnDef<VariantArtifact>[] = [
        { accessorKey: 'id', header: 'Artifact' },
        {
            id: 'compiler',
            header: 'Compiler',
            cell: ({ row }) => `${row.original.compilerBridgeName} ${row.original.compilerBridgeVersion}`
        },
        {
            accessorKey: 'compilerBuildId',
            header: 'Build',
            cell: ({ row }) => <code className="text-xs text-zinc-400">{shortHash(row.original.compilerBuildId)}</code>
        },
        {
            id: 'manifest',
            header: 'Feature manifest',
            cell: ({ row }) => (
                <span className="text-zinc-300">
                    #{row.original.contextFeatureManifestId} v{row.original.contextFeatureManifestVersion}{' '}
                    <code className="text-xs text-zinc-500">{shortHash(row.original.contextFeatureManifestFingerprint)}</code>
                </span>
            )
        },
        {
            accessorKey: 'executionFingerprint',
            header: 'Execution fingerprint',
            cell: ({ row }) => <code className="text-xs text-zinc-400">{shortHash(row.original.executionFingerprint)}</code>
        },
        {
            accessorKey: 'executionGraphContentFingerprint',
            header: 'Graph fingerprint',
            cell: ({ row }) => <code className="text-xs text-zinc-400">{shortHash(row.original.executionGraphContentFingerprint)}</code>
        }
    ];

    if (variantId === null) {
        return (
            <UserLayout containerSize="1400px" title="Idea Lab Variant - Project 314">
                <Callout variant="error">Invalid variant id.</Callout>
            </UserLayout>
        );
    }

    return (
        <UserLayout containerSize="1400px" title={`${variant?.label ?? `Variant #${variantId}`} - Idea Lab - Project 314`}>
            <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                    {variant && (
                        <Link
                            className="text-xs text-zinc-500 hover:text-zinc-300"
                            to={`/user/ideaLab/study/${variant.studyId}?tab=variants`}
                        >
                            ← {study?.name ?? `Study #${variant.studyId}`}
                        </Link>
                    )}
                    <h1 className="text-2xl font-semibold text-zinc-100">
                        {variant?.label ?? `Variant #${variantId}`}
                        <span className="ml-2 align-middle text-sm font-normal text-zinc-500">#{variantId}</span>
                    </h1>
                    {variant && (
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-zinc-400">
                            <span>
                                <code className="text-zinc-300">{variant.variantKey}</code> · {variant.primaryDuration} bars ·{' '}
                                {variant.kind} / {variant.scope}
                            </span>
                            <StatusBadge status={variant.lifecycle} tone={getVariantLifecycleTone(variant.lifecycle)}>
                                {VARIANT_LIFECYCLE_LABELS[variant.lifecycle]}
                            </StatusBadge>
                            {variant.isStudyControl && (
                                <StatusBadge status="control" tone="accent">
                                    Control
                                </StatusBadge>
                            )}
                            {variant.promotionId !== null && (
                                <StatusBadge status="promoted" tone="success">
                                    Promoted
                                </StatusBadge>
                            )}
                        </div>
                    )}
                    {variant?.description && !isDraft && <p className="mt-2 max-w-4xl text-sm text-zinc-400">{variant.description}</p>}
                </div>
                {variant && (
                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                        {isDraft && (
                            <>
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => void navigate(buildIdeaLabRulesEditorPath(variant.id))}
                                >
                                    Edit rules
                                </Button>
                                <Button size="sm" variant="primary" onClick={() => setConfirmFreeze(true)}>
                                    Freeze
                                </Button>
                            </>
                        )}
                        {variant.lifecycle !== 'archived' && (
                            <Button
                                disabled={study === null}
                                isLoading={variation.isPending}
                                size="sm"
                                title="Copy into a new editable draft that keeps this variant as its parent"
                                variant={isFrozen ? 'primary' : 'secondary'}
                                onClick={makeVariation}
                            >
                                Make a variation
                            </Button>
                        )}
                        {isFrozen && (
                            <Button
                                size="sm"
                                title="Create one variation per combination of values you list"
                                variant="secondary"
                                onClick={() => setSweepOpen(true)}
                            >
                                Parameter sweep
                            </Button>
                        )}
                        <ActionMenu items={moreActions} label="More variant actions" />
                    </div>
                )}
            </div>

            {variantQuery.isLoading ? (
                <DataLoadingSpinner text="Loading variant..." />
            ) : variantQuery.error instanceof Error ? (
                <Callout variant="error">{variantQuery.error.message}</Callout>
            ) : variant ? (
                <div className="space-y-6">
                    {freezeResult && freezeResult.variant.id === variant.id && freezeResult.diagnostics.length > 0 && (
                        <Callout
                            title="Compiler diagnostics from the freeze"
                            variant={freezeResult.diagnostics.some((d) => d.severity === 'error') ? 'error' : 'warning'}
                        >
                            <ul className="list-disc space-y-1 pl-5 text-sm">
                                {freezeResult.diagnostics.map((diagnostic) => (
                                    <li key={diagnostic.id}>
                                        <span className="uppercase text-xs text-zinc-500">{diagnostic.severity}</span> {diagnostic.message}
                                        <span className="ml-1 text-xs text-zinc-500">({diagnostic.code})</span>
                                    </li>
                                ))}
                            </ul>
                        </Callout>
                    )}

                    <div className="grid gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
                        <div className="min-w-0 space-y-6">
                            <Card
                                subtitle={
                                    isDraft
                                        ? 'The draft’s current rules. Edit rules opens them in the rule editor.'
                                        : 'The frozen rules every run of this variant evaluated.'
                                }
                                title="Rules"
                            >
                                {ruleDraft?.rootNode ? (
                                    <TradeLogicRuleTree rootNode={ruleDraft.rootNode} />
                                ) : (
                                    <p className="text-sm text-zinc-500">This variant has no rules yet.</p>
                                )}
                            </Card>

                            {variant.parentVariantId !== null && (
                                <Card
                                    subtitle="Every rule setting that differs from the variant this one was copied from; names and descriptions are ignored."
                                    title="Changes from parent"
                                >
                                    <p className="mb-3 text-sm text-zinc-400">
                                        Parent:{' '}
                                        <Link
                                            className="text-blue-300 hover:text-blue-200"
                                            to={`/user/ideaLab/variant/${variant.parentVariantId}`}
                                        >
                                            {parent?.label ?? `#${variant.parentVariantId}`}
                                        </Link>
                                        {parent && <span className="ml-1 text-xs text-zinc-500">#{parent.id}</span>}
                                    </p>
                                    {parentQuery.isLoading ? (
                                        <DataLoadingSpinner text="Loading the parent..." />
                                    ) : parentQuery.error instanceof Error ? (
                                        <Callout variant="error">{parentQuery.error.message}</Callout>
                                    ) : ruleDiff ? (
                                        ruleDiff.length === 0 ? (
                                            <p className="text-sm text-zinc-500">
                                                No rule differences: only the name or description changed from the parent.
                                            </p>
                                        ) : (
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="border-b border-zinc-800 text-left text-[11px] uppercase tracking-wide text-zinc-500">
                                                        <th className="py-1.5 pr-4 font-medium">Setting</th>
                                                        <th className="py-1.5 pr-4 font-medium">Parent</th>
                                                        <th className="py-1.5 font-medium">This variant</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {ruleDiff.map((change) => (
                                                        <tr
                                                            className="border-b border-zinc-800/60 align-top"
                                                            key={`${change.path}|${change.before}|${change.after}`}
                                                        >
                                                            <td className="break-words py-1.5 pr-4 text-zinc-300">{change.path}</td>
                                                            <td className="py-1.5 pr-4 text-zinc-500">
                                                                {change.before ?? <span className="italic">not present</span>}
                                                            </td>
                                                            <td className="py-1.5 font-medium text-zinc-100">
                                                                {change.after ?? (
                                                                    <span className="font-normal italic text-zinc-500">removed</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )
                                    ) : null}
                                </Card>
                            )}
                        </div>

                        <div className="min-w-0 space-y-6">
                            <Card
                                subtitle="Every run in this study that evaluated this variant, newest first."
                                title={`Runs (${variantRuns.length})`}
                            >
                                {runsQuery.isLoading ? (
                                    <DataLoadingSpinner text="Loading runs..." />
                                ) : variantRuns.length === 0 ? (
                                    <p className="text-sm text-zinc-500">
                                        {isFrozen ? 'No run has evaluated this variant yet.' : 'Freeze the variant to include it in a run.'}
                                    </p>
                                ) : (
                                    <ul className="divide-y divide-zinc-800">
                                        {variantRuns.map((run) => (
                                            <li className="py-2 first:pt-0 last:pb-0" key={run.id}>
                                                <div className="flex items-start justify-between gap-3">
                                                    <Link
                                                        className="min-w-0 text-sm font-medium text-blue-300 hover:text-blue-200"
                                                        to={buildRunVariantPath(run.id, variant.id)}
                                                    >
                                                        {run.label}
                                                    </Link>
                                                    <StatusBadge status={run.status} tone={getRunStatusTone(run.status)}>
                                                        {RUN_STATUS_LABELS[run.status]}
                                                    </StatusBadge>
                                                </div>
                                                <div className="mt-0.5 text-xs text-zinc-500">
                                                    #{run.id} · {describeRunWindowShort(run)} · {describeRunLenses(run)} · queued{' '}
                                                    {formatWallClockShort(run.queuedAt ?? run.createdAt)}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </Card>

                            {isDraft && (
                                <Card subtitle="Editable until the variant is frozen." title="Details">
                                    <div className="space-y-3">
                                        <FormInput
                                            label="Label"
                                            maxLength={255}
                                            noMargin
                                            value={label}
                                            onChange={(event) => setLabel(event.target.value)}
                                        />
                                        <FormInput
                                            label="Description"
                                            maxLength={4000}
                                            noMargin
                                            value={description}
                                            onChange={(event) => setDescription(event.target.value)}
                                        />
                                        <FormSelect
                                            helpText="Bar duration the rules are evaluated on."
                                            label="Duration"
                                            noMargin
                                            options={STANDARD_DURATIONS.map((duration) => ({ value: duration, label: duration }))}
                                            value={primaryDuration}
                                            onChange={(event) => setPrimaryDuration(event.target.value as BarDuration)}
                                        />
                                        <div className="flex justify-end">
                                            <Button
                                                disabled={!metadataDirty || label.trim() === ''}
                                                isLoading={updateMutation.isPending}
                                                size="sm"
                                                variant="primary"
                                                onClick={saveMetadata}
                                            >
                                                Save
                                            </Button>
                                        </div>
                                    </div>
                                </Card>
                            )}
                        </div>
                    </div>

                    {variant.promotionId !== null && promotionQuery.data && (
                        <Card
                            subtitle="The inactive strategy created from this variant and its entry-parity verification."
                            title="Promotion"
                        >
                            <PromotionReceiptCard promotion={promotionQuery.data} variantLabel={variant.label} />
                        </Card>
                    )}

                    <Disclosure
                        badge={
                            <span className="text-xs font-normal text-zinc-500">
                                identity · {variant.artifacts.length} compiled {variant.artifacts.length === 1 ? 'artifact' : 'artifacts'} ·
                                raw rules
                            </span>
                        }
                        title="Technical details"
                        variant="card"
                    >
                        <div className="space-y-6">
                            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
                                <Fact label="Document id">{variant.documentId}</Fact>
                                <Fact label="Content fingerprint">{variant.contentFingerprint ?? 'Assigned on freeze'}</Fact>
                                <Fact label="Source">
                                    {variant.sourceDefinitionVersionId !== null
                                        ? `Published version #${variant.sourceDefinitionVersionId}`
                                        : variant.sourceDraftId !== null
                                          ? `Draft #${variant.sourceDraftId}`
                                          : variant.parentVariantId !== null
                                            ? 'Copied from the parent variant'
                                            : 'Authoring payload'}
                                </Fact>
                                <Fact label="Frozen">{formatWallClock(variant.frozenAt)}</Fact>
                                <Fact label="Created">{formatWallClock(variant.createdAt)}</Fact>
                            </dl>

                            <div>
                                <h5 className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">
                                    Compiled artifacts ({variant.artifacts.length})
                                </h5>
                                <p className="mb-2 text-xs text-zinc-500">
                                    One artifact per compiler build. Runs pin the artifact they evaluated, so results stay attributable
                                    after a compiler upgrade.
                                </p>
                                {variant.artifacts.length === 0 ? (
                                    <p className="text-sm text-zinc-500">
                                        {isDraft ? 'Artifacts are compiled when the variant is frozen.' : 'No artifacts yet.'}
                                    </p>
                                ) : (
                                    <DataTable columns={artifactColumns} data={variant.artifacts} search={false} />
                                )}
                            </div>

                            <Disclosure title="Authoring payload (read-only)" variant="card">
                                <JsonBlock value={variant.authoringPayload} />
                            </Disclosure>
                            {variant.publishableDefinition && (
                                <Disclosure title="Compiled definition" variant="card">
                                    <JsonBlock value={variant.publishableDefinition} />
                                </Disclosure>
                            )}
                        </div>
                    </Disclosure>
                </div>
            ) : null}

            <ConfirmationDialog
                confirmText={freezeMutation.isPending ? 'Freezing…' : 'Freeze'}
                confirmVariant="primary"
                isOpen={confirmFreeze}
                message="Freeze this variant? The rules are compiled into an immutable artifact and the metadata locks. Make a variation to try a further change."
                title="Freeze variant"
                onCancel={() => setConfirmFreeze(false)}
                onConfirm={() => variant && !freezeMutation.isPending && freezeMutation.mutate({ variantId: variant.id })}
            />
            <ConfirmationDialog
                confirmText={archiveMutation.isPending ? 'Archiving…' : 'Archive'}
                confirmVariant="danger"
                isOpen={confirmArchive}
                message="Archive this variant? It stays readable, runs already queued still evaluate its frozen artifact, but it cannot join new runs."
                title="Archive variant"
                onCancel={() => setConfirmArchive(false)}
                onConfirm={() => variant && !archiveMutation.isPending && archiveMutation.mutate({ variantId: variant.id })}
            />
            {variant && <VariantGridModal isOpen={sweepOpen} variant={variant} onClose={() => setSweepOpen(false)} />}
        </UserLayout>
    );
};

export default IdeaLabVariant;
